import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useAuth } from "@/components/AuthProvider";
import { getAccessToken } from "@/services/authService";
import { enableAuthStayOnPage } from "@/utils/authStayOnPage";
import { findRideRepository } from "../data/FindRideRepository";
import { tFindRide } from "../locales/FindRideTranslations";
import type {
  AuthMode,
  FindRideOutcome,
  FindRideResultItem,
  FindRideSearchCriteria,
} from "../types/findRide.types";
import {
  clearPendingFindRideSearch,
  readPendingFindRideCriteria,
  readPendingFindRideSearch,
  savePendingFindRideSearch,
} from "../utils/pendingFindRideSearch";

export type UseFindRideSearchParams = {
  setShowAuthModal?: Dispatch<SetStateAction<boolean>>;
  setAuthMode?: Dispatch<SetStateAction<AuthMode>>;
};

function hasUserSession(isAuthenticated: boolean): boolean {
  return isAuthenticated || !!getAccessToken();
}

/**
 * ViewModel: guest-token search; auth modal only for Save & notify (over outcome modal).
 */
export function useFindRideSearch({
  setShowAuthModal,
  setAuthMode,
}: UseFindRideSearchParams = {}) {
  const { isAuthenticated } = useAuth();
  const [criteria, setCriteria] = useState<FindRideSearchCriteria | null>(null);
  const [rides, setRides] = useState<FindRideResultItem[]>([]);
  const [totalRides, setTotalRides] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [outcome, setOutcome] = useState<FindRideOutcome>({ kind: "none" });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notifySaved, setNotifySaved] = useState(false);
  const [isSavingNotify, setIsSavingNotify] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [formPrefill] = useState(() => readPendingFindRideCriteria());
  const searchResumeDone = useRef(false);
  const notifyResumeDone = useRef(false);
  const searchRequestId = useRef(0);
  const wasAuthenticated = useRef(hasUserSession(isAuthenticated));

  const clearOutcome = useCallback(() => {
    searchRequestId.current += 1;
    setIsSearching(false);
    setOutcome({ kind: "none" });
    setSubmitError(null);
    setNotifySaved(false);
    setIsSavingNotify(false);
    setNotifyError(null);
    setRides([]);
    setTotalRides(0);
  }, []);

  const runSearch = useCallback(async (next: FindRideSearchCriteria) => {
    const requestId = ++searchRequestId.current;
    setCriteria(next);
    setSubmitError(null);
    setNotifySaved(false);
    setNotifyError(null);
    setIsSearching(true);
    setOutcome({ kind: "none" });

    try {
      const result = await findRideRepository.search(next);
      if (requestId !== searchRequestId.current) return;

      setRides(result.rides);
      setTotalRides(result.totalRides);
      setOutcome(
        result.totalRides < 1
          ? { kind: "no-rides" }
          : { kind: "rides-available", totalRides: result.totalRides }
      );
    } catch (err) {
      if (requestId !== searchRequestId.current) return;
      const message =
        err instanceof Error && err.message
          ? err.message
          : tFindRide("searchFailed");
      setSubmitError(message);
      setRides([]);
      setTotalRides(0);
      setOutcome({ kind: "none" });
    } finally {
      if (requestId === searchRequestId.current) {
        setIsSearching(false);
      }
    }
  }, []);

  const persistNotify = useCallback(async (next: FindRideSearchCriteria) => {
    setIsSavingNotify(true);
    setNotifyError(null);
    // Keep / restore Save-for-later modal while API runs.
    setOutcome({ kind: "no-rides" });
    try {
      await findRideRepository.saveLaterNotify(next);
      setCriteria(next);
      setNotifySaved(true);
    } catch {
      setNotifyError(tFindRide("saveNotifyFailed"));
      setNotifySaved(false);
    } finally {
      setIsSavingNotify(false);
    }
  }, []);

  /** Search always runs — user token or guest (universal) token. */
  const submitSearch = useCallback(
    (next: FindRideSearchCriteria) => {
      void runSearch(next);
    },
    [runSearch]
  );

  /**
   * Save & notify requires a signed-in session.
   * Guest: keep Save-for-later modal open, show login on top; after login finish API.
   */
  const saveNotifyLater = useCallback(async () => {
    if (!criteria || isSavingNotify || notifySaved) return;

    if (!hasUserSession(isAuthenticated)) {
      notifyResumeDone.current = false;
      savePendingFindRideSearch(criteria, "notify");
      enableAuthStayOnPage();
      setNotifyError(null);
      setAuthMode?.("login");
      setShowAuthModal?.(true);
      return;
    }

    await persistNotify(criteria);
  }, [
    criteria,
    isAuthenticated,
    isSavingNotify,
    notifySaved,
    persistNotify,
    setAuthMode,
    setShowAuthModal,
  ]);

  /** Prefill search from marketing pages (Available Rides / Ride Requests). */
  useEffect(() => {
    if (searchResumeDone.current) return;
    const pending = readPendingFindRideSearch();
    if (!pending || pending.intent !== "search") return;

    searchResumeDone.current = true;
    clearPendingFindRideSearch();
    void runSearch(pending.criteria);
  }, [runSearch]);

  /**
   * After successful login while Save-for-later is pending:
   * close auth → keep outcome open → complete later-notify API.
   */
  useEffect(() => {
    const loggedIn = hasUserSession(isAuthenticated);
    const justLoggedIn = loggedIn && !wasAuthenticated.current;
    wasAuthenticated.current = loggedIn;

    if (!justLoggedIn || notifyResumeDone.current) return;

    const pending = readPendingFindRideSearch();
    if (!pending || pending.intent !== "notify") return;

    notifyResumeDone.current = true;
    clearPendingFindRideSearch();
    setShowAuthModal?.(false);
    void persistNotify(pending.criteria);
  }, [isAuthenticated, persistNotify, setShowAuthModal]);

  const refreshSearch = useCallback(() => {
    if (!criteria || isSearching) return;
    void runSearch(criteria);
  }, [criteria, isSearching, runSearch]);

  const viewAndBook = useCallback(() => {
    setOutcome({ kind: "ride-list" });
  }, []);

  const closeOutcomeModal = useCallback((open: boolean) => {
    if (!open) {
      // Don't dismiss outcome while guest is mid-login for notify.
      const pending = readPendingFindRideSearch();
      if (pending?.intent === "notify" && !hasUserSession(isAuthenticated)) {
        return;
      }
      setOutcome({ kind: "none" });
      setNotifySaved(false);
      setNotifyError(null);
      setIsSavingNotify(false);
    }
  }, [isAuthenticated]);

  const editDetails = useCallback(() => {
    clearOutcome();
  }, [clearOutcome]);

  return {
    criteria,
    formPrefill,
    rides,
    totalRides,
    isSearching,
    outcome,
    submitError,
    notifySaved,
    isSavingNotify,
    notifyError,
    submitSearch,
    clearOutcome,
    refreshSearch,
    viewAndBook,
    closeOutcomeModal,
    editDetails,
    saveNotifyLater,
  };
}
